/* Where did this user come from?
 *
 * TowardsGM had five accounts and no record of how any arrived, which makes
 * every acquisition question unanswerable rather than merely unanswered — you
 * cannot tell whether a channel worked, so you cannot decide to do more of it.
 *
 * Stored as one doc per user at acquisition/{uid}, the same shape as
 * proInterest/{uid}: a flat top-level collection, client-writable, browsable in
 * the Firestore console. This app deliberately writes no users/{uid} document
 * at signup — signup time lives only in Firebase Auth — so there is no existing
 * doc to hang the field on, and inventing one for this would be a bigger change
 * than the fact being recorded.
 *
 * First-touch: written once, when the doc does not yet exist, and never
 * overwritten. The question is what originally brought someone here, and a
 * later visit from a bookmark does not change that. Last-touch would rewrite
 * history on every return and, at this scale, erase the original answer.
 *
 * Privacy: only the referrer's host is kept, never the full URL. The host is
 * the channel; the path is the specific page someone was reading.
 *
 * Mirrors vigilia/shared/src/attribution.ts. The two cannot share code across
 * repos, so the host table and the tests are deliberately kept identical —
 * if one gains a channel, the other should too.
 */

/* Referrer hosts worth collapsing into one name. Without this the same channel
 * reports under several labels (t.co and x.com; news.ycombinator.com and
 * hn.algolia.com), splitting one signal across rows so each half looks too
 * small to act on. An unrecognised host keeps its own name rather than becoming
 * "other", which is where the one link that actually worked would hide. */
const HOST_CHANNELS = [
  [/(^|\.)news\.ycombinator\.com$/, "hackernews"],
  [/(^|\.)hn\.algolia\.com$/, "hackernews"],
  [/(^|\.)(x|twitter)\.com$/, "x"],
  [/(^|\.)t\.co$/, "x"],
  [/(^|\.)reddit\.com$/, "reddit"],
  [/(^|\.)discord\.(com|gg)$/, "discord"],
  [/(^|\.)lichess\.org$/, "lichess"],
  [/(^|\.)chess\.com$/, "chess.com"],
  [/(^|\.)google\./, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)linkedin\.com$/, "linkedin"],
  [/(^|\.)lnkd\.in$/, "linkedin"],
  [/(^|\.)github\.com$/, "github"],
  [/(^|\.)producthunt\.com$/, "producthunt"],
  [/(^|\.)lobste\.rs$/, "lobsters"],
];

/* Our own hosts — a hop between our pages is not a referral, and counting it
 * would make towardsgm.com the answer to every question. */
const OWN_HOSTS = /(^|\.)(towardsgm\.com|towardsgm\.web\.app|towardsgm\.firebaseapp\.com)$/;

/** Bare lowercase host from a full URL or a bare host; undefined if absent. */
export function referrerHost(referrer) {
  const raw = (referrer || "").trim();
  if (!raw) return undefined;
  try {
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`);
    return url.hostname.toLowerCase().replace(/^www\./, "") || undefined;
  } catch {
    return undefined;
  }
}

/* The one place a channel gets its name. An explicit utm_source always wins: it
 * was put there deliberately, and a campaign link clicked from a search result
 * should still be credited to the campaign rather than to Google. */
export function normaliseSource({ utmSource, referrer } = {}) {
  const utm = (utmSource || "").trim().toLowerCase();
  if (utm) return utm.slice(0, 64);

  const host = referrerHost(referrer);
  if (!host) return "direct";
  if (OWN_HOSTS.test(host)) return "direct";
  for (const [pattern, channel] of HOST_CHANNELS) {
    if (pattern.test(host)) return channel;
  }
  return host.slice(0, 64);
}

/* Build the stored record. Absent fields are omitted rather than set to
 * undefined: Firestore rejects an explicit undefined instead of skipping it,
 * which would make the whole write throw. */
export function buildAttribution(input = {}, now = Date.now()) {
  const tag = (value) => (value || "").trim().toLowerCase().slice(0, 64);
  const medium = tag(input.utmMedium);
  const campaign = tag(input.utmCampaign);
  /* Which ad (content) and which audience (term) inside a campaign. Without
   * them a campaign running two ads reads as one, and the better ad can't be
   * told from the worse. */
  const content = tag(input.utmContent);
  const term = tag(input.utmTerm);
  const host = referrerHost(input.referrer);

  return {
    source: normaliseSource(input),
    ...(medium ? { medium } : {}),
    ...(campaign ? { campaign } : {}),
    ...(content ? { content } : {}),
    ...(term ? { term } : {}),
    ...(host && !OWN_HOSTS.test(host) ? { referrerHost: host } : {}),
    at: now,
  };
}

/* The attribution of the page as it was opened: its utm tags, else its
 * referrer. The landing's visit count reads this too, so a visit and the
 * signup it may become are named by the same rules. */
export function attributionFromUrl(search, referrer, now = Date.now()) {
  const params = new URLSearchParams(search || "");
  return buildAttribution({
    utmSource: params.get("utm_source"),
    utmMedium: params.get("utm_medium"),
    utmCampaign: params.get("utm_campaign"),
    utmContent: params.get("utm_content"),
    utmTerm: params.get("utm_term"),
    referrer,
  }, now);
}

const KEY = "tgm_attribution";

/* Browser bits are parameters rather than globals so the logic is testable in
 * plain node — this repo's vitest runs in the "node" environment and adding
 * jsdom just to read two strings would be a dependency for nothing. */
function browserEnv() {
  return {
    search: typeof window === "undefined" ? "" : window.location.search,
    referrer: typeof document === "undefined" ? "" : document.referrer,
    storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
  };
}

/* Read attribution off the current URL and stash it before any OAuth redirect
 * can discard the query string. First-touch: an existing stash is never
 * replaced, so bouncing around the site before signing up keeps the original
 * source. Safe to call more than once. */
export function captureAttribution(env = {}) {
  const { search, referrer, storage } = { ...browserEnv(), ...env };
  if (!storage) return;
  try {
    if (storage.getItem(KEY)) return;
    storage.setItem(KEY, JSON.stringify(attributionFromUrl(search, referrer)));
  } catch {
    /* storage unavailable — attribution is best-effort, never fatal */
  }
}

/* The landing a visitor saw, added to the stash so the signup it becomes
 * (saveAcquisition.js writes the stash as it is) says which landing brought
 * it. First seen wins, like the rest of the stash; a visitor who never sees
 * the landing (the app, a deep link) gets no version. */
export function noteLanding(version, env = {}) {
  const { storage } = { ...browserEnv(), ...env };
  if (!storage || !Number.isInteger(version)) return;
  try {
    const raw = storage.getItem(KEY);
    const stash = raw ? JSON.parse(raw) : null;
    if (!stash || typeof stash.source !== "string" || stash.landing) return;
    storage.setItem(KEY, JSON.stringify({ ...stash, landing: version }));
  } catch {
    /* storage unavailable — best-effort, like the stash itself */
  }
}

/** The stashed attribution, or undefined. Not consumed: the write may retry. */
export function readAttribution(env = {}) {
  const { storage } = { ...browserEnv(), ...env };
  if (!storage) return undefined;
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    /* A stash with no source is corrupt; writing it would put a document into
     * Firestore that the metrics function cannot group. */
    return parsed && typeof parsed.source === "string" ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/* The Google Play link's install referrer: the one place Play Console can tell
 * where an install came from, since the Android app reads no referrer itself.
 * A visitor a tagged link brought keeps that link's tags, so Play Console
 * counts installs per ad; anyone else is credited to the site's landing. */
export function playStoreUrl(attribution) {
  const a = attribution?.campaign ? attribution : undefined;
  const params = new URLSearchParams({
    utm_source: a?.source || "towardsgm.com",
    utm_medium: a?.medium || "landing",
    ...(a?.campaign ? { utm_campaign: a.campaign } : {}),
    ...(a?.content ? { utm_content: a.content } : {}),
    ...(a?.term ? { utm_term: a.term } : {}),
  });
  return `https://play.google.com/store/apps/details?id=com.towardsgm.app&referrer=${encodeURIComponent(params.toString())}`;
}
